import { useContext } from 'react'
import { GlobalDataContext, TemplateContext, View, evaluateTemplateValue } from '@ea-lab/reactive-json'
import { toList } from '../../../utils/collections'

/**
 * Generic "render these already-authored fragments, in this data-driven
 * order" element. Fills a real gap in reactive-json : a `Switch` picks a
 * template PER ITEM from a key on the item itself, but the key it dispatches
 * on must be a literal string at author time — there is no way to say "read
 * the field named by THIS loop's current value" (`~.field` needs `field` to
 * be static YAML, not data). So a reorderable table — column order coming
 * from the store, one differently-authored `<th>`/`<td>` per column — can't
 * be expressed as a Switch over the order array. `OrderedColumns` is the
 * escape hatch : `order` says which fragments to render and in what
 * sequence, `columns` is a plain map of key → fragment (each one exactly the
 * `<th>`/`<td>` you'd have written inline before). It renders each present
 * key's fragment at the SAME path/datafield this element itself received —
 * no new template context, no new store path — so a `~.field` inside a
 * fragment still reads the surrounding row exactly as if it had been written
 * inline.
 *
 * A key present in `order` but absent from `columns` is silently skipped
 * (lets `order` carry columns a stale saved view remembers that this render
 * of the page no longer defines).
 *
 * YAML usage — same `columns:` map reused for the header row and every body
 * row (only what differs is passed as `props.columns`) :
 *   - type: tr
 *     content:
 *       - { type: th, ... }                 # fixed, non-reorderable columns
 *       - type: OrderedColumns
 *         order: ~~.columns                 # active column keys, in display order
 *         columns:
 *           email: { type: th, content: "E-mail" }
 *           phone: { type: th, content: "Téléphone" }
 *   ...
 *   templates:
 *     dealRow:
 *       - type: tr
 *         content:
 *           - { type: td, ... }
 *           - type: OrderedColumns
 *             order: ~~.columns
 *             columns:
 *               email: { type: td, content: ~.email }
 *               phone: { type: td, content: ~.phone }
 */
export const OrderedColumns = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const order = evaluateTemplateValue({ valueToEvaluate: props.order, globalDataContext, templateContext })
  // `toList` : sous un `DataFilter`, l'ordre des colonnes arrive en objet
  // indexé — le tableau se rendrait alors SANS aucune colonne optionnelle.
  const keys = toList(order)
  const columns = props.columns && typeof props.columns === 'object' ? props.columns : {}

  return keys.map((key) =>
    columns[key] != null ? (
      <View key={key} path={path} datafield={datafield} props={columns[key]} />
    ) : null
  )
}
